import React, { useEffect, useRef, useState } from 'react';

// ---- Scroll-reveal: kinderen faden rustig omhoog zodra ze in beeld komen ----
export const Reveal: React.FC<{ delay?: number; className?: string; children: React.ReactNode }> = ({ delay = 0, className = '', children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${inView ? 'in' : ''} ${className}`.trim()} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
};

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ---- Teller die naar zijn eindwaarde toe telt (NL-notatie via decimals) ----
export const CountUp: React.FC<{ to: number; decimals?: number; duration?: number; suffix?: string }> = ({ to, decimals = 0, duration = 900, suffix = '' }) => {
  const [v, setV] = useState(reducedMotion() ? to : 0);
  useEffect(() => {
    if (reducedMotion()) { setV(to); return; }
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  const shown = decimals ? v.toFixed(decimals).replace('.', ',') : String(Math.round(v));
  return <>{shown}{suffix}</>;
};

// ---- Voortgangsring: cirkel die zich bij verschijnen vult ----
export const ProgressRing: React.FC<{ pct: number; size?: number; stroke?: number; color: string; track?: string; label?: React.ReactNode }> = ({
  pct, size = 96, stroke = 9, color, track = '#EFEAE3', label,
}) => {
  const [p, setP] = useState(reducedMotion() ? pct : 0);
  useEffect(() => {
    if (reducedMotion()) { setP(pct); return; }
    const id = window.setTimeout(() => setP(pct), 60);
    return () => window.clearTimeout(id);
  }, [pct]);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(0, Math.min(100, p)) / 100)}
          style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(.2,.7,.2,1)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>{label}</div>
    </div>
  );
};
